/*
 * -----------------------------------------------------------------------
 *  <copyright file="study-dashboard-item.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useMemo } from 'react';

import { useBoolean } from 'hooks';
import ActionsMenu from 'views/components/core/actions-menu';

import './styles.scss';

interface IStudyDashboardItem {
    panel: any;
    first: boolean;
    last: boolean;
    disabled: boolean;
    setChildPanel?: Function;
    selected?: boolean;
    moveItem?: Function;
    removeItem?: Function;
    showBorder?: boolean;
    hasChild?: boolean;
    fieldsToShow?: {
        width: number | string;
        field: string;
    }[];
    actions?: any;
    index: number;
}

const StudyDashboardItem = ({
    panel,
    first,
    last,
    disabled,
    setChildPanel,
    selected,
    moveItem,
    removeItem,
    showBorder,
    hasChild,
    fieldsToShow,
    actions,
    index,
}: IStudyDashboardItem) => {
    const showRemoveBorder = useBoolean(false);
    const [menu, setMenu] = useState(null);
    let timeout: any;

    useEffect(
        () => () => {
            clearTimeout(timeout);
        },
        [],
    );

    useEffect(() => {
        if (actions)
            setMenu(
                [...actions].map((item: any) => ({
                    ...item,
                    onClick: item.onClick || remove,
                })),
            );
    }, [actions]);

    const remove = () => {
        if (!disabled) {
            showRemoveBorder.setTrue();
            timeout = setTimeout(() => {
                removeItem(panel, index);
                if (selected && setChildPanel) setChildPanel(null);
                showRemoveBorder.setFalse();
            }, 600); // For creating effect of delay by design
        }
    };

    return (
        <div
            className={`dashboard-panel-item ${last ? 'last' : ''} ${first ? 'first' : ''}`}
            data-testid={`dashboard-panel-item-${index}`}
        >
            {menu ? (
                <div className="menu-items-dashboard-item">
                    <ActionsMenu
                        actions={menu}
                        height="100%"
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    />
                </div>
            ) : (
                <span
                    className={`trash-icon ${disabled ? 'disabled' : ''}`}
                    onClick={remove}
                    data-testid="delete-button"
                >
                    <FontAwesomeIcon icon={['far', 'trash']} className="icon" />
                </span>
            )}
            {fieldsToShow ? (
                <span
                    className={`dashboard-item-panel-name ${
                        selected || showBorder || showRemoveBorder.value ? 'border' : ''
                    }
                        ${hasChild ? 'has-child' : ''} ${fieldsToShow ? 'no-pd' : ''}`}
                    onClick={() => {
                        if (setChildPanel) setChildPanel(panel);
                    }}
                >
                    {fieldsToShow.map((item: any, i: number) => (
                        <p
                            key={item.field}
                            className={`${i > 0 ? 'border' : ''} field-to-show`}
                            style={{ width: item.width }}
                        >
                            {panel[item.field]}
                            {item.active === false ? (
                                <FontAwesomeIcon
                                    style={{ color: '#7f7f7f', fontSize: 14 }}
                                    icon={['fal', 'times-circle']}
                                    className="times-circle icon"
                                />
                            ) : null}
                        </p>
                    ))}
                </span>
            ) : (
                <p
                    className={`dashboard-item-panel-name ${
                        selected || showBorder || showRemoveBorder.value ? 'border' : ''
                    } ${hasChild ? 'has-child' : ''}`}
                    onClick={() => {
                        if (setChildPanel) setChildPanel(panel);
                    }}
                >
                    {panel.name}
                    {panel.active === false ? (
                        <FontAwesomeIcon
                            style={{ color: '#7f7f7f', fontSize: 14, marginLeft: 3 }}
                            icon={['fal', 'times-circle']}
                            className="times-circle icon"
                        />
                    ) : null}
                </p>
            )}
            <div className="top-down-arrows">
                <span
                    className={`top-arrow ${first || disabled ? 'disabled' : ''}`}
                    onClick={() => {
                        if (!disabled && !first) moveItem(index, index - 1);
                    }}
                    data-testid="top-arrow"
                >
                    <FontAwesomeIcon icon={['fal', 'chevron-up']} className="icon" />
                </span>
                <span
                    className={`down-arrow ${last || disabled ? 'disabled' : ''}`}
                    onClick={() => {
                        if (!disabled && !last) moveItem(index, index + 1);
                    }}
                    data-testid="down-arrow"
                >
                    <FontAwesomeIcon icon={['fal', 'chevron-down']} className="icon" />
                </span>
            </div>
        </div>
    );
};

export default StudyDashboardItem;
